// @ts-nocheck
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LoggerService } from '@way/theme-host/services/logger.service';
import { CommonModule } from '@angular/common';
import { PackageService } from '@way/theme-host/services/package.service';
import { Package } from '@way/theme-host/shared/types';
import { NotificationService } from '@way/theme-host/services/notification.service';
import {
  ModernPageHeaderComponent as PageHeaderComponent,
  NavigationItem,
  Logo,
  CTAButton,
} from '../shared/page-header/page-header.component';
import { buildHeaderCtaButton } from '../shared/header-cta.util';
import { ModernPageFooterComponent as PageFooterComponent } from '../shared/footer/footer.component';
import {
  SharedPackageSelectionModalComponent,
  SelectedPackage,
} from '@way/website-themes/themes/shared/package-selection-modal/package-selection-modal.component';
import { PACKAGE_ICON_FALLBACK } from '@way/theme-host/shared/constants/website-builder-assets';
import { resolveFooterQuickLinks } from '@way/theme-host/shared/utils/footer-quick-links.util';

// Import WebsiteElement type from home-page-preview
export interface WebsiteElement {
  id: string;
  type:
    | 'header'
    | 'hero'
    | 'section'
    | 'testimonials'
    | 'services'
    | 'booking'
    | 'contact'
    | 'footer'
    | 'packages'
    | 'coupons'
    | 'fleets'
    | 'express-details';
  content: any;
  selected?: boolean;
}

@Component({
  selector: 'app-modern-express-wash-packages-preview',
  standalone: true,
  providers: [NotificationService],
  imports: [
    CommonModule,
    PageHeaderComponent,
    PageFooterComponent,
    SharedPackageSelectionModalComponent,
  ],
  templateUrl: './express-wash-packages-preview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./express-wash-packages-preview.component.scss'],
})
export class ModernExpressWashPackagesPreviewComponent implements OnInit, OnChanges {
  @Input() pageData: any = null;
  @Input() sectionVisibility: Record<string, boolean> = {};
  @Input() pages: any[] = [];
  @Input() currentPageType = '';
  @Input() elements: WebsiteElement[] = [];
  @Input() deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  @Input() primaryColor: string | null = null; // Primary color from theme
  @Input() headingColor: string | null = null; // Heading color from branding
  @Input() paragraphColor: string | null = null; // Paragraph/description color from branding
  @Input() headingFont: string | null = null; // Heading font from branding
  @Input() bodyFont: string | null = null; // Body font from branding
  @Output() elementSelect = new EventEmitter<WebsiteElement>();
  @Output() navigationClick = new EventEmitter<string>();

  packages: Package[] = [];
  loading = false;
  error: string | null = null;
  readonly shimmerSlots = [0, 1, 2] as const;

  // Page data from JSON
  headerData: any = null;
  packageSectionData: any = null;
  footerData: any = null;
  pageTitleData: any = null;

  // Package Selection Modal
  showPackageSelectionModal = false;
  selectedPackageForModal: Package | null = null;

  constructor(
    private logger: LoggerService,
    private packageService: PackageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadPageContent();
  }

  /**
   * Handles Angular SimpleChanges to update page content, header navigation, and package/section data when relevant inputs change.
   * @example
   * gOnChanges({ pageData: { firstChange: false }, pages: {...}, elements: {...} })
   * undefined
   * @param {{SimpleChanges}} {{changes}} - Angular SimpleChanges object containing previous and current values for inputs (e.g., pageData, pages, currentPageType, elements).
   * @returns {{void}} No return value; updates component state by loading page content, updating header navigation, and refreshing package/section data as needed.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageData'] && !changes['pageData'].firstChange) {
      this.loadPageContent();
    }
    // Update navigation when pages or currentPageType changes
    if ((changes['pages'] || changes['currentPageType']) && this.headerData) {
      this.updateHeaderNavigation();
    }
    // Reload page content when elements change (for background color updates)
    if (changes['elements'] && !changes['elements'].firstChange) {
      // Force reload of packageSectionData to get updated background color
      const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
      const sections = page?.sections || {};
      this.packageSectionData = sections.package_section || {};
      this.pageTitleData = sections['page-title'] || sections.hero_section || {};
    }
  }

  /**
   * Load and initialize page content and related UI state for the Express Wash Packages preview.
   * @example
   * this.loadPageContent()
   * undefined
   * @returns {void} Updates component state (headerData, packageSectionData, footerData, pageTitleData) and triggers header navigation and package loading.
   **/
  private loadPageContent(): void {
    if (!this.pageData) return;

    this.logger.debug('?? Loading Express Wash Packages page content:', this.pageData);

    // Extract sections from page data - handle both editor and preview formats
    // Editor format: pageData.page.sections
    // Preview format: pageData.json_data.page.sections
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    const sections = page?.sections || this.pageData?.sections || {};

    this.headerData = sections.header || {};
    this.packageSectionData = sections.package_section || {};
    this.footerData = sections.footer || {};
    // Load page-title section (for heading and description)
    this.pageTitleData = sections['page-title'] || sections.hero_section || {};

    this.logger.debug('Header data:', this.headerData);
    this.logger.debug('Package section data:', this.packageSectionData);
    this.logger.debug('Footer data:', this.footerData);
    this.logger.debug('Page title data:', this.pageTitleData);

    // Update header navigation to match current pages
    this.updateHeaderNavigation();

    // Load packages from API
    this.loadPackages();
  }

  /**
   * Update the component's header navigation from the pages array and ensure a default CTA button exists.
   * @example
   * this.updateHeaderNavigation()
   * // undefined
   * @returns {void} Updates this.headerData.navigation by filtering visible pages, mapping each to {label, link, active}, and ensures this.headerData.ctaButton has default values if missing.
   **/
  private updateHeaderNavigation(): void {
    if (!this.headerData) this.headerData = {};

    // Generate navigation from pages if pages are provided
    // Filter out inactive pages (visible: false means active: false)
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      this.headerData.navigation = activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        active: page.pageType === this.currentPageType,
      }));
      this.logger.debug(
        'Updated header navigation for Express Wash Packages:',
        this.headerData.navigation
      );
    }

    // Ensure CTA button exists
    if (!this.headerData.ctaButton && !this.headerData.button) {
      this.headerData.ctaButton = {
        text: '',
        link: '#booking',
        backgroundColor: '#1B5BFF',
        textColor: '#FFFFFF',
      };
    }
  }

  /**
   * Loads car wash packages asynchronously and updates the component state (this.packages, this.loading, this.error).
   * @example
   * loadPackages()
   * undefined
   * @returns {{void}} No return value; performs side effects by setting loading, packages, and error on the component.
   **/
  private loadPackages(): void {
    this.loading = true;
    this.error = null;

    this.packageService.getCarWashPackages().subscribe({
      next: response => {
        this.logger.debug('? Packages loaded:', response);
        if (response.status && response.data) {
          this.packages = response.data;
          this.logger.debug('Packages count:', this.packages.length);
        } else {
          this.error = 'No packages available';
        }
        this.loading = false;
      },
      error: err => {
        this.logger.error('? Error loading packages:', err);
        this.error = 'Failed to load packages';
        this.loading = false;
      },
    });
  }

  // Format price for display
  formatPrice(price: number | string | undefined): string {
    if (!price) return '$0.00';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return `$${numPrice.toFixed(2)}`;
  }

  // Get package image or fallback
  getPackageImage(pkg: Package): string {
    // Use image from API if available, otherwise fall back to static placeholder
    if (pkg.image && pkg.image.trim() !== '') {
      return pkg.image;
    }
    return PACKAGE_ICON_FALLBACK;
  }

  // Get package image fit/scaletype
  /**
   * Determine CSS object-fit value for a package image based on the package's configured scaletype.
   * @example
   * etPackageImageFit({ id: 'basic' })
   * 'cover'
   * @param {{Package}} {{pkg}} - Package object containing at minimum an id used to find its configuration.
   * @returns {{string}} CSS object-fit value ('cover', 'contain', 'fill' or 'scale-down').
   **/
  getPackageImageFit(pkg: Package): string {
    // Check package_section.packages array for this package's scaletype
    const packageSectionPackages = this.packageSectionData?.packages || [];
    const packageConfig = packageSectionPackages.find((p: any) => p.id === pkg.id);

    if (packageConfig?.scaletype) {
      // Map API scaletype to CSS object-fit values
      const scaletype = packageConfig.scaletype;
      if (scaletype === 'fill' || scaletype === 'stretch') {
        return 'fill';
      } else if (scaletype === 'cover') {
        return 'cover';
      } else if (scaletype === 'contain') {
        return 'contain';
      } else if (scaletype === 'fit') {
        return 'scale-down';
      }
    }

    // Default to cover if not specified
    return 'cover';
  }

  // Handle image loading errors
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = PACKAGE_ICON_FALLBACK;
  }

  selectedPlanType: Record<string, 'single' | 'monthly'> = {};

  // Check if package has single wash
  hasSingleWash(pkg: Package): boolean {
    return !!pkg.wash_types?.includes('single_wash');
  }

  // Check if package has monthly membership
  hasMonthlyMembership(pkg: Package): boolean {
    return !!pkg.wash_types?.includes('monthly_membership');
  }

  // Check if package has unlimited washes
  isUnlimited(pkg: Package): boolean {
    return pkg.type === 'unlimited' || pkg.package_name?.toLowerCase().includes('unlimited');
  }

  // Get selected plan type
  getSelectedPlanType(pkg: Package): 'single' | 'monthly' {
    if (this.selectedPlanType[pkg.id]) {
      return this.selectedPlanType[pkg.id];
    }
    // Default: if it has single, use single. Else if it has monthly, use monthly.
    if (this.hasSingleWash(pkg)) {
      return 'single';
    } else if (this.hasMonthlyMembership(pkg)) {
      return 'monthly';
    }
    return this.isUnlimited(pkg) ? 'monthly' : 'single';
  }

  // Select plan type
  selectPlanType(packageId: string, planType: 'single' | 'monthly', event: Event): void {
    event.stopPropagation();
    this.selectedPlanType[packageId] = planType;
    this.cdr.detectChanges();
  }

  // Get display price (use single_list_price or monthly_list_price)
  getListPrice(pkg: Package): string {
    const planType = this.getSelectedPlanType(pkg);
    return planType === 'monthly' ? pkg.monthly_list_price : pkg.single_list_price;
  }

  // Get strikeoff price (discounted price)
  getStrikeoffPrice(pkg: Package): string {
    const planType = this.getSelectedPlanType(pkg);
    return planType === 'monthly' ? pkg.monthly_strikeoff_price : pkg.single_strikeoff_price;
  }

  /**
   * Check if a section should be visible based on section visibility settings
   */
  isSectionVisible(sectionKey: string): boolean {
    if (!this.sectionVisibility || Object.keys(this.sectionVisibility).length === 0) {
      return true;
    }
    return this.sectionVisibility[sectionKey] !== false;
  }

  /**
   * Get package section element for selection
   */
  getPackageSectionElement(): WebsiteElement | undefined {
    return this.elements.find(
      el =>
        el.type === 'packages' ||
        el.id === 'packages' ||
        (el as any).originalKey === 'package_section'
    );
  }

  /**
   * Get package section background color
   */
  getPackageSectionBackgroundColor(): string | null {
    // Priority 1: Check element content (most up-to-date, updated immediately)
    const packageElement = this.getPackageSectionElement();
    const packageContent = packageElement?.content as any;
    if (packageContent?.backgroundColor) {
      return packageContent.backgroundColor;
    }
    // Priority 2: Check packageSectionData (from API/pageData)
    if (this.packageSectionData?.backgroundColor) {
      return this.packageSectionData.backgroundColor;
    }
    // Priority 3: Check page-level theme background color
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    if (page?.theme?.background_color) {
      return page.theme.background_color;
    }
    // Priority 4: Check pageData directly
    const sections = page?.sections || {};
    const packageSection = sections.package_section || {};
    if (packageSection.backgroundColor) {
      return packageSection.backgroundColor;
    }
    // Default fallback
    return null;
  }

  /**
   * Get page title section background color (same as package section)
   */
  getPageTitleBackgroundColor(): string | null {
    // Use the same background color as the package section
    return this.getPackageSectionBackgroundColor();
  }

  /**
   * Handle package section click
   */
  /**
   * Get page title text
   */
  getPageTitle(): string {
    if (this.pageTitleData?.title) {
      if (typeof this.pageTitleData.title === 'string') {
        return this.pageTitleData.title;
      } else if (this.pageTitleData.title?.label) {
        return this.pageTitleData.title.label;
      }
    }
    return 'Express Wash Packages';
  }

  /**
   * Get page subtitle/description text
   */
  getPageSubtitle(): string {
    if (this.pageTitleData?.description) {
      return this.pageTitleData.description;
    } else if (this.pageTitleData?.subtitle) {
      return this.pageTitleData.subtitle;
    }
    return 'Choose from our range of washes that suit all needs and budgets.';
  }

  /**
   * Get title styles for ngStyle binding
   */
  getTitleStyles(): any {
    if (!this.pageTitleData?.title) return {};

    const title = this.pageTitleData.title;
    const titleStyleObj = title.style?.text_style;

    if (!titleStyleObj || typeof titleStyleObj !== 'object') return {};

    const styles: any = {};
    if (titleStyleObj.fontWeight) styles['font-weight'] = titleStyleObj.fontWeight;
    if (titleStyleObj.fontStyle) styles['font-style'] = titleStyleObj.fontStyle;
    if (titleStyleObj.textDecoration) styles['text-decoration'] = titleStyleObj.textDecoration;
    if (titleStyleObj.textAlign) styles['text-align'] = titleStyleObj.textAlign;

    return styles;
  }

  /**
   * Handle package section click
   */
  onPackageSectionClick(event: Event): void {
    // Stop event propagation to prevent conflicts
    event.stopPropagation();

    const packageElement: WebsiteElement = {
      id: 'packages',
      type: 'packages',
      content: {
        ...(this.packageSectionData || {}),
        title: this.getPageTitle(),
        subtitle: this.getPageSubtitle(),
        pageTitleData: this.pageTitleData,
        backgroundColor: this.getPackageSectionBackgroundColor(),
      },
      selected: true,
      originalKey: 'package_section',
    } as any;

    this.logger.debug('📦 Package section clicked, emitting element:', packageElement);
    this.elementSelect.emit(packageElement);
  }

  /**
   * Retrieve the header logo data from the component's header element.
   * @example
   * etHeaderLogo()
   * { image: 'https://example.com/logo.png', text: 'Site Name', link: '/' }
   * @returns {{Logo}} Returns a Logo object with image, text and link properties; defaults to { text: 'Logo', link: '/' } when no logo is configured.
   **/
  getHeaderLogo(): Logo {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.logo) {
      return {
        image: headerContent.logo.image || headerContent.logo.url || '',
        text: headerContent.logo.text || 'Logo',
        link: headerContent.logo.link || '/',
      };
    }
    return { text: 'Logo', link: '/' };
  }

  /**
   * Retrieve the header navigation items from the component's header content or generate them from pages when absent.
   * @example
   * etHeaderNavigation()
   * [
   *   { label: "Home", link: "#home", active: true },
   *   { label: "Services", link: "#services", active: false }
   * ]
   * @returns {NavigationItem[]} Array of navigation items for the header.
   */
  getHeaderNavigation(): NavigationItem[] {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.navigation && Array.isArray(headerContent.navigation)) {
      return headerContent.navigation;
    }
    // Fallback to generating from pages
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      return activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        active: page.pageType === this.currentPageType,
      }));
    }
    return [];
  }

  /**
   * Retrieve the header CTA button configuration from the component's header element, using defaults when absent.
   * @example
   * etHeaderCTAButton()
   * { text: 'Book Wash', link: '#', backgroundColor: '#1B5BFF', textColor: '#FFFFFF' }
   * @returns {{CTAButton|null}} CTA button object containing text, link, backgroundColor and textColor, or null if the header element is not available.
   **/
  getHeaderCTAButton(): CTAButton | null {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    return buildHeaderCtaButton(headerContent?.ctaButton);
  }

  getHeaderBackgroundColor(): string | null {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.backgroundColor) {
      return headerContent.backgroundColor as string;
    }
    return null;
  }

  /**
   * Get footer quick links from active pages
   * Filters out pages where visible === false (active === false)
   */
  getFooterQuickLinks(): { label: string; link: string }[] {
    // First, try to get from footer element content
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const storedLinks = Array.isArray(footerContent?.quickLinks) ? footerContent.quickLinks : [];
    return resolveFooterQuickLinks(storedLinks, this.pages);
  }

  /**
   * Extracts and normalizes social links from the component's footer content into objects with platform, url, and icon fields.
   * @example
   * etFooterSocialLinks()
   * [{ platform: 'Facebook', url: 'https://facebook.com/example', icon: 'facebook' }]
   * @returns {{Array<{ platform: string; url: string; icon: string }>}} Returns an array of normalized social link objects.
   **/
  getFooterSocialLinks(): { platform: string; url: string; icon: string }[] {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.socialLinks && Array.isArray(footerContent.socialLinks)) {
      return footerContent.socialLinks.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }
    return [];
  }

  getFooterSocialLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    return footerContent?.socialLinksTitle || 'Connect with us';
  }

  getFooterQuickLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    return footerContent?.quickLinksTitle || 'Quick links';
  }

  getFooterCopyright(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.copyright) {
      return footerContent.copyright;
    }
    return `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`;
  }

  /**
   * Retrieve the footer logo from the footer element content or fall back to the header logo or a default.
   * @example
   * this.etFooterLogo()
   * { text: "way", image: "https://example.com/logo.png" }
   * @returns {{ text?: string; image?: string }} | undefined Footer logo object with optional text and image, or undefined if not available.
   **/
  getFooterLogo(): { text?: string; image?: string } | undefined {
    // Get logo from footer element content
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;

    if (footerContent?.logo) {
      const logo = footerContent.logo;
      return {
        text: logo.text || 'way',
        image: logo.image || logo.url || '',
      };
    }

    // Fallback to header logo if footer logo not available
    const headerLogo = this.getHeaderLogo();
    if (headerLogo && headerLogo.image) {
      return {
        text: headerLogo.text,
        image: headerLogo.image,
      };
    }
    return { text: 'way' };
  }

  getFooterBackgroundColor(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.backgroundColor) {
      return footerContent.backgroundColor;
    }
    return '#000920';
  }

  getHeaderElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'header');
  }

  getFooterElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'footer');
  }

  onHeaderClick(): void {
    const headerElement = this.getHeaderElement();
    if (headerElement) {
      this.onElementClick(headerElement);
    }
  }

  onElementClick(element: WebsiteElement): void {
    if (!element) return;
    this.elementSelect.emit(element);
  }

  onFooterClick(event: Event): void {
    event.stopPropagation();
    const footerElement = this.getFooterElement();
    if (footerElement) {
      if (!footerElement.type || footerElement.type !== 'footer') {
        footerElement.type = 'footer';
      }
      if (!footerElement.id || footerElement.id !== 'footer') {
        footerElement.id = 'footer';
      }
      this.onElementClick(footerElement);
    }
  }

  onNavigationClick(pageType: string): void {
    this.navigationClick.emit(pageType);
  }

  /**
   * Get primary color from theme or use default
   */
  getPrimaryColor(): string {
    return this.primaryColor || '#1B5BFF';
  }

  /**
   * Get heading color from branding or use default
   */
  getHeadingColor(): string {
    return this.headingColor || '#1E3A8A';
  }

  /**
   * Get paragraph color from branding or use default
   */
  getParagraphColor(): string {
    return this.paragraphColor || '#64748B';
  }

  /**
   * Get heading font from branding or use default
   */
  getHeadingFont(): string {
    return this.headingFont || 'Inter';
  }

  /**
   * Get body font from branding or use default
   */
  getBodyFont(): string {
    return this.bodyFont || 'Inter';
  }

  onBuyNowClick(pkg: Package, event: Event): void {
    event.stopPropagation();
    this.selectedPackageForModal = pkg;
    this.showPackageSelectionModal = true;
  }

  onClosePackageModal(): void {
    this.showPackageSelectionModal = false;
    this.selectedPackageForModal = null;
  }

  onPackageSelected(selection: SelectedPackage): void {
    this.selectedPackageForModal = selection;
    this.logger.debug('📦 Package selected:', selection);
  }

  getPackagesForModal(): Package[] {
    if (this.selectedPackageForModal) {
      return [this.selectedPackageForModal];
    }
    return this.packages;
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
